import { type ConfiguredQuickEntryItem, type QuickEntryItem } from "../types/models";
import { type ThunkAction } from "redux-thunk";
import { type RootState } from "~/app/store";
import { type AnyAction } from "~/app/store/types";
import { type LineItemBuilder } from "~/utils/cartUpdating/LineItemUpdate";
import { type LineItem, PackageEntrySource } from "~/features/cart/cart/models/CartData";
import { getNextUID } from "~/common/utils/uid";
import * as WeightUtils from "~/utils/WeightUtils";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { newChangeObject } from "~/utils/cartUpdating/common";
import { groupByFn, isNonNull, toMapFn } from "~/common/utils/reducers";
import { refreshDeliveriesForProduct } from "~/utils/cartUpdating/delivery/refreshEdd";
import {
  MSG_COULD_NOT_PRESELECT_PACKAGE,
  MSG_PACKAGE_CHANGED,
  refreshAvailabilityLoadNonContract,
} from "~/utils/cartUpdating/refreshPackage";
import { refreshPriceInfoData } from "~/utils/cartUpdating/refreshPriceInfoData";
import { refreshDeliveryDate } from "~/utils/cartUpdating/delivery/refreshDate";
import { validatePackage, validateQty } from "~/utils/cartUpdating/validations/validateGeneralLineItem";
import { buildCartMaterialNos } from "~/utils/cartUpdating/common";
import { validateEntireProductQty } from "~/utils/cartUpdating/validations/validateGeneralProduct";
import { sendToHybris, sendToRedux } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import { INVALID_ENTRY_ID } from "../constants/constants";
import { hasProductConfigured } from "../utils/helpers";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getProductData, isKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { requestBaseProductData } from "~/domains/product/redux/actions";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export const addProductsToCartAction = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return (dispatch, getState) => {
    if (userIsCustomer()) {
      return dispatch(addProductsToCartActionB2B(items));
    } else {
      return dispatch(addProductsToCartActionB2C(items));
    }
  };
};

export const addProductsToCartActionB2B = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    // Sanity check - items without products should not be passed here
    const configuredEntries = items.filter(hasProductConfigured);

    if (configuredEntries.length === 0) {
      return;
    }

    const state = getState();

    const getPriceRef = (item: ConfiguredQuickEntryItem) => (item.priceReference === "-" ? "" : item.priceReference);

    const promises = configuredEntries
      .flatMap((item) => {
        const { promise, current } = ProductService.getProductDescriptor(item.product.code, getPriceRef(item), {
          fetch: true,
        });
        return [
          promise,
          ...(isKITProduct(current.base)
            ? current.base.optionalParts.map(
                (subProduct) =>
                  ProductService.getProductDescriptor(subProduct.product.code, getPriceRef(item), { fetch: true })
                    .promise,
              )
            : []),
        ];
      })
      .filter(isNonNull);

    const productDescriptors = await Promise.all(promises);

    const descriptorsByProductCode = groupByFn(productDescriptors, (item) => item.base.code);

    const descriptorsByCodeByPriceRef: Dict<Dict<ProductDescriptor>> = {};
    Object.entries(descriptorsByProductCode).forEach(([productCode, descriptors]) => {
      descriptorsByCodeByPriceRef[productCode] = toMapFn(descriptors, (item) => item.priceReference);
    });

    const transform = (item: ConfiguredQuickEntryItem) =>
      transformItem(item, descriptorsByCodeByPriceRef[item.product?.code || ""][getPriceRef(item)]);

    const transformedEntries = configuredEntries.map(transform);
    let allNewLineItems = transformedEntries.map((_) => _.lineItem);
    const allNewOptionalParts: LineItemBuilder[] = transformedEntries.flatMap((_) => _.subLineItems);

    allNewLineItems = allNewLineItems.concat(allNewOptionalParts);

    const newLineItemsByCode = groupByFn(
      allNewLineItems,
      (item) => item.getProductCode(),
      (item) => item,
    );

    const existingLineItemsByCode = groupByFn(
      Object.values(state.cart.packageEntriesById).map(newChangeObject),
      (item) => item.getProductCode(),
      (item) => item,
    );

    // These need to go through a full refresh
    const merged = Object.keys(newLineItemsByCode).reduce(
      (map, productCode) => {
        map[productCode] = [...(existingLineItemsByCode[productCode] || []), ...newLineItemsByCode[productCode]];
        return map;
      },
      {} as Dict<LineItemBuilder[]>,
    );

    Object.keys(merged).forEach((productCode) => {
      orchestrateRefreshProductForATC(
        merged[productCode],
        descriptorsByCodeByPriceRef[productCode],
        buildCartMaterialNos(state),
      );
    });

    const allLineItems = Object.values(merged).flat();

    // We are expected to preselect the items, so no message
    allNewLineItems.forEach((item) => {
      item.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
      item.removeMessage(MSG_PACKAGE_CHANGED.key);
    });

    dispatch(sendToRedux(allLineItems));
    await Promise.all(
      Object.values(merged).map((lineItems) => {
        return dispatch(sendToHybris(lineItems.filter((item) => !allNewOptionalParts.includes(item))));
      }),
    );
    dispatch(cartPageLoadThunk());
  };
};

export const addProductsToCartActionB2C = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    // Sanity check - items without products should not be passed here
    const configuredEntries = items.filter(hasProductConfigured);

    if (configuredEntries.length === 0) {
      return;
    }

    const state = getState();

    const products = await Promise.all(
      configuredEntries.map((item) => dispatch(requestBaseProductData(item.product.code))),
    );
    const productByCode = toMapFn(products, (item) => item.code);

    const transform = (item: ConfiguredQuickEntryItem) => transformItem(item, productByCode[item.product.code]);

    const transformedEntries = configuredEntries.map(transform);
    let allNewLineItems = transformedEntries.map((_) => _.lineItem);
    const allNewOptionalParts: LineItemBuilder[] = transformedEntries.flatMap((_) => _.subLineItems);

    allNewLineItems = allNewLineItems.concat(allNewOptionalParts);

    const newLineItemsByCode = groupByFn(
      allNewLineItems,
      (item) => item.getProductCode(),
      (item) => item,
    );

    const existingLineItemsByCode = groupByFn(
      Object.values(state.cart.packageEntriesById).map(newChangeObject),
      (item) => item.getProductCode(),
      (item) => item,
    );

    // These need to go through a full refresh
    const merged = Object.keys(newLineItemsByCode).reduce(
      (map, productCode) => {
        map[productCode] = [...(existingLineItemsByCode[productCode] || []), ...newLineItemsByCode[productCode]];
        return map;
      },
      {} as Dict<LineItemBuilder[]>,
    );

    const allLineItems = Object.values(merged).flat();

    // We are expected to preselect the items, so no message
    allNewLineItems.forEach((item) => {
      item.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
      item.removeMessage(MSG_PACKAGE_CHANGED.key);
    });

    // Force item.hasUpdate to be true
    allLineItems.forEach((item) => {
      item.set("productCode", item.getProductCode());
    });

    dispatch(sendToRedux(allLineItems));
    await Promise.all(Object.values(merged).map((lineItems) => dispatch(sendToHybris(lineItems))));
    dispatch(cartPageLoadThunk());
  };
};

function orchestrateRefreshProductForATC(
  allLineItems: LineItemBuilder[],
  descriptorsByPriceReference: Dict<ProductDescriptor>,
  cartMaterialNos?: Set<string>,
) {
  const anyDescriptor = Object.values(descriptorsByPriceReference)[0];
  const availability = anyDescriptor.availability;

  const lineItemsByType = groupByFn(
    allLineItems,
    (item) => item.get("source"),
    (item) => item,
  );
  const lineItemsWithUpdateablePrice = [
    ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
    ...(lineItemsByType[PackageEntrySource.BULK] || []),
  ];
  const lineItemsWithUpdateableEDD = [
    ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
    ...(lineItemsByType[PackageEntrySource.BULK] || []),
    ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
  ];

  // 2.1. First availability => We may need to preselect packages.
  lineItemsWithUpdateablePrice.forEach((lineItem) =>
    refreshAvailabilityLoadNonContract(lineItem, availability!, anyDescriptor),
  );

  // 2.2. Then prices.
  refreshPriceInfoData(allLineItems, descriptorsByPriceReference);

  // 3. Refresh the Estimated Delivery Date.
  refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability!);

  // 4. Refresh requested delivery date
  allLineItems.forEach(refreshDeliveryDate);

  // 5. Various qty and package validations
  allLineItems.forEach(validateQty);
  validateEntireProductQty(allLineItems, Object.values(descriptorsByPriceReference)[0], cartMaterialNos);
  allLineItems.forEach(validatePackage);
}

function transformItem(
  data: QuickEntryItem,
  productDescriptor: ProductDescriptor | ProductData,
): { lineItem: LineItemBuilder; subLineItems: LineItemBuilder[] } {
  const product = getProductData(productDescriptor);
  const item: LineItem = {
    ...makeEmptyLineItem(product.code),
    comment: data.comment,
    deliveryDate: data.requestedDeliveryDate,
    packCode: data.packCode ? data.packCode : INVALID_PACKAGE_CODE,
    quantity: data.quantity,
    weight: product.weight ?? WeightUtils.zero(),
    priceReference: data.priceReference,

    restrictedPackage: isPackageRestrictionEnabled() ? !!data.packCode : false,
  };

  return {
    lineItem: newChangeObject(item),
    subLineItems: [],
  };
}

const makeEmptyLineItem = (productCode: string): LineItem => {
  return {
    pk: INVALID_ENTRY_ID,
    id: getNextUID(),
    cartEntryPk: INVALID_ENTRY_ID,
    productCode: productCode,
    groupNumbers: [],
    comment: "",
    deliveryDate: new Date(),
    manuallySetRDD: true,
    packSize: 1,
    packCode: INVALID_PACKAGE_CODE,
    quantity: 1,
    weight: WeightUtils.zero(),
    netPrice: undefined,
    valid: true,
    entryNumber: null,
    primaryWarehouseName: "",
    primaryWarehouseCode: "",
    messages: [],
    disableEdit: false,
    source: PackageEntrySource.DEFAULT,
    showComments: false,
    priceReference: "",

    edd: null,
    isInStock: null,

    // Salesman fields
    internalComment: "",
    priceOverriden: false,

    // quote fields
    quoteNumber: "",
    quoteLine: "",
    minimumQty: 1,
    contractNumber: "",
    contractLine: "",
    estimatedShippingDate: null,
    quoteExpiryDate: null,
    original: true,

    // UI Fields
    isPackagingSelectionOpen: false,

    restrictedPackage: false,

    customerOrderNumber: "",
    countryOfOrigin: "",

    tariffSurcharge: false,
  };
};
